import {
  action,
  configure,
  decorate,
  observable,
  reaction,
  runInAction,
  toJS,
} from 'mobx';
import SignalRMiddleware from 'shared/utils/SignalRHelpers';
import {
  clearStorage,
  getRouteFromPathName,
  getStoredAuctionIdByPath,
  getStoredAuctionLaneIdByPath,
  getStoredValue,
  removeStoredAuctionLaneIdByPath,
  setCookie,
  setStoredAuctionLaneIdByPath,
} from 'shared/utils/localStorage';
import {
  formatCurrency,
  getTrimmedString,
} from '../components/project/ClerkScreen/shared/utils/common';
import { getWsHost } from '../endpoints';

import { formatDateTimeMS } from '../shared/utils/dateTime';
import {
  getListDataById,
  mapToObj,
  mergeArrayObjects,
  toBoolean,
  toTitleCase,
} from './utils';

// import { storageKeys } from './DebuggerStore';
configure({ enforceActions: 'always' });

export default class WebSocketStore {
  constructor(
    AuthStore,
    AuctionStore,
    BidStore,
    MessageStore,
    ProviderStore,
    DebuggerStore,
  ) {
    runInAction('WebSocketStore', () => {
      this.store = this;
      this.debuggerStore = DebuggerStore;

      // console.log('debuggerStore', this.debuggerStore);
      this.authStore = AuthStore;
      this.auctionStore = AuctionStore;
      this.bidStore = BidStore;
      this.messageStore = MessageStore;
      this.providerStore = ProviderStore;
      this.connection = null;
      this.auctionStore.connected = false;
      this.error = null;
      this.reconnecting = false;
      this.softLoadingVisible = false;
      this.networkConnected = true; //not used...
      this.networkCheckTimeout = false;
      this.pingInterval = false;
      this.keyGen = () => 6 * Math.random();
      this.invalidAuctionError = false;

      console.log('WebsocketStore > init > call handleReconnect');
      this.handleReconnect();
    });

    reaction(
      () => this.auctionStore.connected,
      (state) => {
        console.log('REACTION: auctionStore.connected', state);
        this.auctionStore.setProperty('connected', state);
        this.auctionStore.initialize();

        this.debuggerStore.log({
          component: 'WebSocketStore',
          message: state ? 'Connected' : 'Disconnected',
          messageType: state ? 'log' : 'error',
          method: 'REACTION: auctionStore.connected',
        });
      },
    );

    reaction(
      () => this.error,
      (error) => {
        if (
          ['1006', '1011'].filter((code) => error.message.indexOf(code) > -1)
            .length > 0
        ) {
          this.authStore.setProperty(
            'errorMessage',
            'Invalid user name or password',
          );
        }
        this.toggleSoftLoading(false);
        this.messageStore.handleMessagingObject({
          message: error.message,
          type: -1,
        });
        this.messageStore.handleMessaging(error.message, error, null);

        this.debuggerStore.log({
          component: 'WebSocketStore',
          message: JSON.stringify(error),
          messageType: this.debuggerStore.messageType.ERROR,
          method: 'REACTION: error',
        });
      },
    );
  }

  setProperty = (key, value) => {
    this[key] = value;
  };

  handleReconnect = async () => {
    try {
      const sessionToken = getStoredValue('simulcast_session');
      if (sessionToken) {
        this.reconnecting = true;
        console.log('handleReconnect - attempt to connect');
        await this.connect(null, null, sessionToken);

        /** Protects against getting caught in a loading spinner loop */
        const timeout = setTimeout(() => {
          runInAction('handleReconnect', () => {
            console.log('handleReconnect - set this.reconnecting = false');
            this.reconnecting = false;
            clearTimeout(timeout);
          });
        }, 1000);
      }
    } catch (error) {
      this.debuggerStore.log({
        component: 'WebSocketStore',
        message: error,
        messageType: this.debuggerStore.messageType.ERROR,
        method: 'handleReconnect',
      });
    }
  };

  // TODO: Throws a 'Lane is already running error'

  handleRejoin = async () => {
    console.log('handleRejoin');
    try {
      await this.handleRejoinAuction();
      const laneID = getStoredAuctionLaneIdByPath();
      if (laneID) {
        runInAction('Reconnect', () => {
          console.log('handleRejoin - setting this.reconnecting = true');
          this.reconnecting = true;
        });
        console.log(
          'handleRejoin - try to change lane; will invoke ChangeLane',
        );
        await this.sendMessage({
          method: 'ChangeLane',
          payload: [laneID],
        });
        setTimeout(() => this.sendSetLaneRunningMessage(), 1000);
      }
    } catch (error) {
      this.debuggerStore.log({
        component: 'WebSocketStore',
        message: error,
        messageType: this.debuggerStore.messageType.ERROR,
        method: 'handleRejoin',
      });
    }
  };

  handleRejoinAuction = async () => {
    try {
      const auctions = this.auctionStore.auctionList;
      const auctionID = getStoredAuctionIdByPath();

      const isValidAuctionID = auctions.filter(
        (item) => item.auctionID === auctionID,
      );

      if (isValidAuctionID.length) {
        if (auctionID) {
          runInAction('Reconnect', () => {
            console.log(
              'handleRejoinAuction - setting this.reconnecting = true',
            );
            this.reconnecting = true;
          });
          console.log(
            'handleRejoinAuction - try to join auction; will invoke JoinAuction',
          );

          await this.sendMessage({
            method: 'JoinAuction',
            payload: [auctionID],
          });
        }
      } else {
        console.log(
          'handleRejoinAuction - JoinAuction skipped since no auction id selected',
          {
            auctions: JSON.stringify(toJS(auctions)),
            auctionID,
            isValidAuctionID,
          },
        );
      }
    } catch (error) {
      this.debuggerStore.log({
        component: 'WebSocketStore',
        message: error,
        messageType: this.debuggerStore.messageType.ERROR,
        method: 'handleRejoinAuction',
      });
    }
  };

  toggleSoftLoading = (value) => {
    this.softLoadingVisible = value;

    const timeout = setTimeout(() => {
      runInAction('toggleLoading', () => {
        this.softLoadingVisible = false;
        clearTimeout(timeout);
      });
    }, 1000);
  };

  connect = (username, password, sessionToken) => {
    this.toggleSoftLoading(true);

    if (this.invalidAuctionError) {
      window.location.href = '/login';
      clearStorage();
    }

    const version = window.localStorage.getItem('cacheVersion');
    this.connection = this.OpenSocket(
      username,
      password,
      sessionToken,
      version,
    );
    this.connection
      .start()
      .then(async () => {
        if (!this.invalidAuctionError) {
          window.sessionStorage.removeItem('loginError');
          console.log('connection started');

          console.log('initialize debuggerStore');
          this.debuggerStore.initialize();
          runInAction('connect', () => {
            this.auctionStore.setProperty('connected', true);
            this.listeners();
            this.messageStore.handleMessagingObject({
              message: 'Connected',
              type: -2,
            });
            this.messageStore.handleMessaging('Connected');
            if (this.auctionStore.onClerkScreen) {
              this.bidStore.initializeBidding();
            }

            this.debuggerStore.log({
              component: 'WebSocketStore',
              message: 'Connected',
              messageType: this.debuggerStore.messageType.INFO,
              method: 'connect',
            });
          });

          const route = toTitleCase(getRouteFromPathName());

          if (!this.pingInterval && route === 'Clerk') {
            this.pingInterval = setInterval(() => {
              try {
                if (
                  !getStoredAuctionIdByPath() ||
                  !getStoredValue('simulcast_session')
                ) {
                  // console.log(
                  //   '*****disconnect initiated***** line 166',
                  //   route,
                  //   getStoredAuctionIdByPath(),
                  //   getStoredValue('simulcast_session'),
                  // );
                  this.disconnect();
                }

                this.sendMessage({
                  method: 'ping',
                  payload: [],
                });
              } catch (error) {
                console.error('connect', error);

                this.debuggerStore.log({
                  component: 'WebSocketStore',
                  message: error.toString(),
                  messageType: this.debuggerStore.messageType.ERROR,
                  method: 'connect > pingInterval',
                });
              }
              this.networkCheckTimeout = setTimeout(() => {
                this.auctionStore.setProperty('networkConnected', false);
                const connected =
                  this.auctionStore.connected &&
                  this.auctionStore.networkConnected;

                this.debuggerStore.log({
                  component: 'WebSocketStore',
                  message: connected
                    ? 'Network connected'
                    : 'Network disconnected, retrying...',
                  messageType: connected ? 'log' : 'error',
                  method: 'connect > networkCheckTimeout',
                });
              }, 1000);
            }, 2000);
          }

          if (this.auctionStore.auctionList.length === 0) {
            console.log(
              'connect - call GetAuctionList (COMMENTING OUT THE CALL SINCE STARTING CONNECTION ALREADY EMITS AN AUCTION LIST)',
            );
            // this.sendMessage({
            //   method: 'GetAuctionList',
            // });
          } else {
            console.log('connect - skip calling GetAuctionList');
          }
          this.toggleSoftLoading(false);
        }
      })
      .catch((error) => {
        console.error('websocket error', error);
        if (error.message.includes('1011')) {
          this.authStore.setProperty(
            'errorMessage',
            'Invalid username/password',
          );
          console.error('*****disconnect initiated***** line 198', error);
          this.disconnect();
          clearStorage();
        }
        runInAction('connect error', () => {
          console.error('connection error');
        });
        this.toggleSoftLoading(false);

        this.debuggerStore.log({
          component: 'WebSocketStore',
          message: error?.toString(),
          messageType: this.debuggerStore.messageType.ERROR,
          method: 'connect > start connection',
        });
      });

    this.connection.onclose((error) => {
      console.log('connection closed', error ?? '- no error');
      this.auctionStore.setProperty('connected', false);

      this.debuggerStore.log({
        component: 'WebSocketStore',
        message: error ? JSON.stringify(error) : 'Close connection',
        messageType: error ? 'error' : 'log',
        method: 'connection.onclose',
      });
    });

    this.connection.onreconnecting((error) => {
      console.log('reconnecting', error ?? '- no error');
      // TODO We're not getting a specific error code back for invalid username/password. This needs to
      // TODO be added server side.
      if (error.message.includes('1011')) {
        this.auctionStore.setProperty('connected', false);
        this.authStore.setProperty('errorMessage', 'Invalid username/password');
        console.error('*****disconnect initiated***** line 220', error);
        this.disconnect();
      }
      this.messageStore.handleMessaging('Reconnecting...');
      this.messageStore.handleMessagingObject({
        message: 'Reconnecting...',
        type: -2,
      });

      this.debuggerStore.log({
        component: 'WebSocketStore',
        message: error ? error.toString() : 'Reconnecting',
        messageType: error ? 'error' : 'log',
        method: 'connection.onreconnecting',
      });
    });

    this.connection.onreconnected(async () => {
      console.log('connection state', this.connection.state);
      this.messageStore.handleMessaging('Connection re-established...');
      this.messageStore.handleMessagingObject({
        message: 'Connection re-established...',
        type: -2,
      });

      this.debuggerStore.log({
        component: 'WebSocketStore',
        message: 'Reconnected',
        messageType: this.debuggerStore.messageType.INFO,
        method: 'connection.onreconnected',
      });
    });
  };

  // TODO: Does not appear that endLane() is triggering LaneStatusChanged. When clicking End Lane button, the lane status = 'Running'
  endLane = async () => {
    try {
      this.auctionStore.initializeLane();
      await this.sendMessage({
        method: 'SetLaneEndedStatus',
        payload: [this.auctionStore.getAuctionLaneIdForInvocation],
      });
    } catch (error) {
      console.error('endLane', error);

      this.debuggerStore.log({
        component: 'WebSocketStore',
        message: JSON.stringify(error),
        messageType: this.debuggerStore.messageType.ERROR,
        method: 'endLane',
      });
    }
  };

  endEvent = () => {
    this.bidStore.endItem();
    this.bidStore.setProperty('isOpenForBidding', false);
  };

  disconnect = () => {
    console.log('*****disconnect*****');
    clearInterval(this.pingInterval);
    this.pingInterval = false;
    window.sessionStorage.removeItem('userName');
    this.messageStore.handleMessaging('Disconnected');
    this.messageStore.handleMessagingObject({
      message: 'Disconnected',
      type: -2,
    });
    runInAction('disconnect', () => {
      clearStorage();
      this.reconnecting = false;
      this.auctionStore.setProperty('onClerkScreen', false);
    });

    this.debuggerStore.log({
      component: 'WebSocketStore',
      message: 'Disconnect',
      messageType: this.debuggerStore.messageType.ERROR,
      method: 'disconnect',
    });

    this.connection.stop().catch((error) => {
      runInAction('disconnect error', () => {
        console.error('***error disconnecting****', error);
        this.error = error;
        this.auctionStore.connected = false;
      });

      this.debuggerStore.log({
        component: 'WebSocketStore',
        message: JSON.stringify(error),
        messageType: this.debuggerStore.messageType.ERROR,
        method: 'connection.stop',
      });
    });
  };

  OpenSocket = (username, password, sessionToken = null, version) => {
    let route = toTitleCase(getRouteFromPathName());

    if (route === 'Login') {
      route = 'LaunchPad';
    }

    const userNameToUse =
      !username || username === 'null' ? '&Username=' : `&Username=${username}`;
    const passwordToUse =
      !password || password === 'null' ? '&Password=' : `&Password=${password}`;

    let url = `?Name=${route}${userNameToUse}${passwordToUse}&MemberType=Superuser&MemberRole=${route}&uiVersion=${version}&host=${window.location.hostname}`;
    if (sessionToken) {
      url += `&SessionToken=${sessionToken}`;
    }
    const host = getWsHost();
    const socket = new SignalRMiddleware(host, url).openSocket();
    return socket;
  };

  emit = async (object) => {
    // console.log('emit', object.method);

    const isTypingMessage =
      this.bidStore.auction.focusOnIndvMessage ||
      this.bidStore.auction.focusOnLaneMessage ||
      this.bidStore.auction.focusOnPinMessage;

    const isInClerkingMode = !toBoolean(
      window.localStorage.getItem('monitorChatOnly'),
    );

    // console.log('isTypingMessage', { isTypingMessage, isInClerkingMode });

    this.receiveMessage({ method: object.method, payload: object.payload });

    switch (object.method) {
      case 'AuctionChanged': {
        if (this.auctionStore.onClerkScreen) {
          this.bidStore.initializeBidding();
        }
        const auctionID = object.payload[0];
        this.auctionStore.setAuction(auctionID);
        if (!this.reconnecting) {
          removeStoredAuctionLaneIdByPath();
        }
        break;
      }
      case 'AuctionInfo': {
        this.auctionStore.setAuctionInfo(object.payload[1]);
        setCookie('streamer_auction', object.payload[1]);
        //document.cookie = `streamer_auction=${JSON.stringify(object.payload[1]).replace(/[\u0080-\uFFFF]+/g, '')}; domain=npauctions.com`;
        break;
      }
      case 'AuctionList': {
        // if (this.auctionStore.auctionList.length === 0) {
        // console.log(
        //   'AuctionList - received auction list',
        //   JSON.stringify(object.payload[0]),
        // );
        this.auctionStore.setAuctionList(object.payload[0]);

        // console.log('AuctionList - call handleRejoinAuction');
        this.handleRejoinAuction();
        // }
        break;
      }
      case 'AuctionMessage': {
        this.bidStore.handleServerBlockMessage(object.payload[1]);
        this.messageStore.handleMessagingObject(object.payload[1]);
        this.messageStore.handleMessaging(
          `(${object.payload[1].type}) ${object.payload[1].message}`,
        );
        break;
      }
      case 'AuctionSettings': {
        this.auctionStore.setAuctionSettings(object.payload[1]);
        break;
      }
      case 'BidAmountChanged': {
        if (object.payload[1]) {
          this.bidStore.setProperty(
            'prevStatusBgColor',
            this.bidStore.auction.statusBgColor,
          );
          this.bidStore.setProperty(
            'prevStatusColor',
            this.bidStore.auction.statusColor,
          );

          this.bidStore.updateBlock(object.payload[1]);
          if (this.bidStore.auction.isOpenForBiddingFromStartingBidAmount) {
            this.bidStore.setProperty(
              'isOpenForBiddingFromStartingBidAmount',
              false,
            );
            await this.sendMessage({
              method: 'OpenBidding',
              payload: [this.auctionStore.getAuctionLaneIdForInvocation],
            });
          }
        }

        break;
      }
      case 'BiddingClosed': {
        this.sendMessage({
          method: 'UiMessageToClerks',
          payload: [this.auctionStore.getAuctionLaneIdForInvocation, null],
        });
        break;
      }
      case 'BiddingOpened': {
        const { lotNumber, productState } = object.payload[1];
        this.bidStore.updateBlock(object.payload[1]);
        this.auctionStore.updateRunListItemState(lotNumber, productState);

        if (isInClerkingMode && !isTypingMessage) {
          this.sendFocusBidButtonMessage();
        }
        break;
      }
      case 'Disconnected': {
        console.warn(`${object.method} > NOT IMPLEMENTED`);
        break;
      }
      case 'GlobalPinCleared': {
        console.warn(`${object.method} > NOT IMPLEMENTED`);
        break;
      }
      case 'GlobalPinSet': {
        console.warn(`${object.method} > NOT IMPLEMENTED`);
        break;
      }
      case 'LaneBidIncrementChanged': {
        const { bidIncrement } = object.payload[1];
        this.auctionStore.setLaneSetting('DefaultBidIncrement', bidIncrement);
        break;
      }
      case 'LaneChanged': {
        // note: keep an eye on the timing with resetting lane messages
        this.messageStore.handleClearMessagingObject();
        setStoredAuctionLaneIdByPath(object.payload[0]);

        console.log('object.payload[1]', object.payload[1])
        this.auctionStore.setVirtualAuctioneerStatus(object.payload[1]);
        this.auctionStore.setLane(object.payload[0]);
        if (this.auctionStore.onClerkScreen) {
          this.bidStore.initializeBidding();
        }

        this.bidStore.setProperty('previousDecision', {
          decision: null,
          lotNumber: null,
          productState: null,
          winner: null,
        });
        await this.sendMessage({
          method: 'GetRunList',
          payload: [this.auctionStore.getAuctionLaneIdForInvocation],
        });

        runInAction('Reconnect', () => {
          this.reconnecting = false;
        });

        break;
      }
      case 'LaneClients': {
        this.messageStore.setOnlineListeners(object.payload[1].laneClients);
        this.auctionStore.setProperty(
          'laneClientList',
          object.payload[1].laneClients,
        );
        break;
      }
      case 'LaneList': {
        this.auctionStore.initializeLane();
        this.auctionStore.setLaneList(object.payload[1]);
        setCookie('streamer_lanes', object.payload[1]);
        //document.cookie = `streamer_lanes=${JSON.stringify(object.payload[1]).replace(/[\u0080-\uFFFF]+/g, '')}; domain=npauctions.com`;
        const auctionLaneId =
          getStoredAuctionLaneIdByPath() ??
          this.auctionStore?.lastAuctionLaneId;

        if (auctionLaneId) {
          await this.sendMessage({
            method: 'ChangeLane',
            payload: [this.auctionStore.getAuctionLaneIdForInvocation],
          });
        }

        break;
      }
      case 'LaneMediaSettings': {
        this.auctionStore.setLaneMediaSettings(object.payload[1]);
        setCookie('streamer_media', object.payload[1]);
        //document.cookie = `streamer_media=${JSON.stringify(object.payload[1]).replace(/[\u0080-\uFFFF]+/g, '')}; domain=npauctions.com`;
        break;
      }
      case 'LaneMessage': {
        console.log('LaneMessage', object.payload[1]);
        // deciding previous decision
        const [NO_SALE_TYPE, SOLD_TYPE, SUBJECT_TO_TYPE] = [5, 6, 7];
        if (
          [NO_SALE_TYPE, SOLD_TYPE, SUBJECT_TO_TYPE].includes(
            object.payload[1].type,
          ) &&
          object.payload[1].message.indexOf('Currently') === -1
        ) {
          const { message, type } = object.payload[1];

          console.log('current bid', this.bidStore.auction.currentBid)

          const getDecisionText = () => {
            if (type === NO_SALE_TYPE) return 'NO SALE';
            if (type === SOLD_TYPE)
              return `SOLD $ ${formatCurrency(
                this.bidStore.auction.currentBid,
              )}`;
            return `SUBJECT TO $ ${formatCurrency(
              this.bidStore.auction.currentBid,
            )}`;
          };

          const getWinningBidder = () => {
            if (type === NO_SALE_TYPE) return null;
            const parts = message.split(' to ');
            if (parts.length < 2) {
              console.warn('PreviousDecision: Could not parse winner from message:', message);
              return null;
            }
            return getTrimmedString(parts[1]);
          };

          const getLotNumber = () => {
            let regex = /Lot\s#(\d+)/;
            let match = message.match(regex);
            if (match) {
              return `Lot # ${match[1]}`;
            }
            console.warn('PreviousDecision: Could not parse lot number from message:', message);
            return null;
          }

          const getProductState = () => {
            if (object.payload[1].type === NO_SALE_TYPE) return 4;
            if (object.payload[1].type === SOLD_TYPE) return 3;
            return 2;
          };

          const previousDecision = {
            decision: getDecisionText(),
            lotNumber : getLotNumber(),
            productState: getProductState(),
            winner: getWinningBidder(),
          };

          const updateRunListAndStats = (laneID, lotNumber, productState) => {
            const statusedItem = { laneID, lotNumber, productState };
            const runList = this.auctionStore.runList;
            const mergedRunList = mergeArrayObjects(
              runList,
              statusedItem,
              'lotNumber',
            );
            this.auctionStore.setRunList(mergedRunList);

            const stats = new Map();
            [0, 1, 2, 3, 4].map((state) =>
              stats.set(
                state,
                getListDataById(mergedRunList, 'productState', state).length,
              ),
            );
            this.auctionStore.setProperty('runListStats', mapToObj(stats));
            //console.log('LaneMessage', object.payload, { stats: this.auctionStore.runListStats, statusedItem, runList, mergedRunList, index: runList.findIndex(item=>item.lotNumber === lotNumber), filtered: mergedRunList.filter(item=>item.lotNumber === lotNumber) });
          };

          updateRunListAndStats(
            object.payload[0],
            this.bidStore.auction.lotNumber,
            getProductState(),
          );

          this.bidStore.setProperty('previousDecision', previousDecision);
          this.bidStore.setProperty('invokingEngine', true);

          //this.sendToAuditLog(JSON.stringify(this.debuggerStore.getLog({ target: 'engine' })));
          if (this.debuggerStore.getDebugBooleanValue('deleteLogOnNextUnit')) {
            this.debuggerStore.deleteLog();
          }
        }

        // clear auction log if we change lanes
        if (getStoredAuctionLaneIdByPath() !== object.payload[0]) {
          this.messageStore.handleClearMessagingObject();
        }

        this.bidStore.handleServerBlockMessage(object.payload[1]);
        this.messageStore.handleMessagingObject(object.payload[1]);
        this.messageStore.handleMessaging(
          `(${object.payload[1].type}) ${object.payload[1].message}`,
        );

        // we are moving to next lot number
        if (object.payload[1].message === 'Cleared OnBlock') {
          console.log('cleared on block');
          this.auctionStore.initializeLane();
          this.bidStore.initializeBidding();

          await this.sendMessage({
            method: 'ClearLaneStatus',
            payload: [this.auctionStore.getAuctionLaneIdForInvocation],
          });
        }

        break;
      }
      case 'LaneMetrics': {
        this.auctionStore.setLaneMetrics(object.payload[1]);
        break;
      }
      case 'LanePinCleared': {
        this.messageStore.setLanePinnedMessage(object.payload[0], null);
        this.messageStore.handleDirectMessage(
          '',
          this.auctionStore.auctionID,
          this.auctionStore.auctionLaneId,
          'ClearGlobalPin',
          null,
        );
        break;
      }
      case 'LanePinSet': {
        this.messageStore.setLanePinnedMessage(
          object.payload[0],
          object.payload[1],
        );
        break;
      }
      case 'LaneSettings': {
        this.auctionStore.setProperty('laneSettings', object.payload[1]);
        break;
      }
      case 'LaneStatusChanged': {
        this.auctionStore.setProperty('laneStatus', object.payload[1]);
        runInAction('Clear temp block status', () => {
          if (object.payload[1] === 'Running')
            this.bidStore.setProperty('tempBlockStatus', null);
        });

        break;
      }
      case 'LayoutSettings': {
        this.auctionStore.setLayoutSettings(object.payload[0]);
        break;
      }
      case 'MediaStatusChanged': {
        this.auctionStore.toggleLaneMedia(object.payload[1]);
        break;
      }
      case 'MessageFromClient': {
        const [laneId, data] = object.payload;
        this.messageStore.setOnlineBiddersForChat(data?.memberName);
        this.messageStore.handleDirectMessage(
          data,
          this.auctionStore.auctionID,
          laneId,
          'MessageFromClient',
          data.lotNumber,
          data.userType,
        );
        break;
      }

      case 'MessageFromClerks': {
        const [laneId, data] = object.payload;
        const { account, lotNumber, member, memberName, message } = data;

        const payload = {
          account,
          lotNumber,
          member,
          memberName,
          message,
        };

        try {
          const { auctionID } = this.auctionStore;
          const {
            auction: { lotNumber: itemNumber },
          } = this.bidStore;

          this.messageStore.handleDirectMessage(
            payload,
            auctionID,
            laneId,
            'MessageToClient',
            lotNumber || itemNumber,
          );
        } catch (error) {
          console.error('MessageFromClerks', {
            error,
            ...payload,
            ...data,
          });
        }
        break;
      }

      case 'OnBlockChanged': {
        const onBlockData = object.payload[1];

        this.bidStore.setProperty('invokingEngine', false);

        // TODO: ARE WE NOW, ALWAYS GETTING AN ARRAY PAYLOAD WITH AT LEAST A LANE ID AT INDEX 0???
        if (!onBlockData) {
          console.log('OnBlockChanged');
          this.bidStore.endItem();
          this.bidStore.initializeBidding();
          this.messageStore.clearSellerAction(); // Reset any previous seller actions
          if (isInClerkingMode && !isTypingMessage) {
            this.sendFocusOpenItemMessage();
          }
          return;
        }

        if (onBlockData) {
          await this.sendMessage({
            method: 'GetProduct',
            payload: [
              this.auctionStore.getAuctionLaneIdForInvocation,
              onBlockData.lotNumber,
            ],
          });

          this.messageStore.setSellerAction({
            sellerAccount: onBlockData.sellerAccount,
            sellerAccountName: onBlockData.sellerAccountName,
            sellerAction: onBlockData.sellerAction,
            sellerActionAccount: onBlockData.sellerActionAccount,
            sellerActionAccountName: onBlockData.sellerActionAccountName,
            sellerActionAmount: onBlockData.sellerActionAmount,
            sellerActionMessage: onBlockData.sellerActionMessage,
          });

          this.bidStore.updateBlock(onBlockData);

          if (this.bidStore.auction.isOpenForBiddingFromStartingBidAmount) {
            this.bidStore.setProperty(
              'isOpenForBiddingFromStartingBidAmount',
              false,
            );
            await this.sendMessage({
              method: 'OpenBidding',
              payload: [this.auctionStore.getAuctionLaneIdForInvocation],
            });
          }
        }
        if (isInClerkingMode && !isTypingMessage) {
          if (this.bidStore.auction.lotNumber !== '') {
            if (this.bidStore.auction.isOpenForBidding) {
              this.sendFocusBidButtonMessage();
            } else {
              this.sendFocusStartingBidAmountMessage();
            }
          } else {
            this.sendFocusOpenItemMessage();
          }
        }
        break;
      }
      case 'Product': {
        this.bidStore.setProduct(object.payload[1]);
        break;
      }
      case 'ProviderSettings': {
        this.providerStore.setProviderSettings(object.payload[0]);
        break;
      }
      case 'RunList': {
        this.auctionStore.initializeProducts();
        const arr = object.payload[1];
        if (arr.length) {
          this.auctionStore.setRunList(arr);
        }
        break;
      }
      case 'pong': {
        if (!this.auctionStore.networkConnected) {
          this.auctionStore.setProperty('networkConnected', true);
        }
        if (this.networkCheckTimeout || this.invalidAuctionError) {
          clearTimeout(this.networkCheckTimeout);
        }
        break;
      }

      case 'SellerAction': {
        const message = object.payload[1];
        this.messageStore.setSellerAction(message);
        break;
      }

      case 'SessionSettings': {
        this.authStore.setSettings(object.payload[0]);
        break;
      }
      case 'OnBlockCountdown': {
        const [LaneID, data] = object.payload[0];

        console.log('OnBlockCountdown', data)
      }
      case 'SystemMessage': {
        const { message, type } = object.payload[0];
        console.log('SystemMessage', message, type);

        if (!message && !type) {
          return;
        }

        this.messageStore.setSystemMessage(message, type);

        if (
          message?.toUpperCase().includes('LOGIN ERROR: MEMBER NOT FOUND') ||
          message?.toUpperCase().includes('LOGIN ERROR: INVALID USERNAME')
        ) {

          this.authStore.setProperty(
            'errorMessage',
            'Invalid username/password',
          );
          this.connection
            .stop()
            .then(() => {
              clearTimeout(this.networkCheckTimeout);
              clearStorage();
              this.connection = null;
            })
            .catch((error) => console.error('SystemMessage', error));
          window.sessionStorage.setItem(
            'loginError',
            'Invalid username/password',
          );
          window.location.href = `/login`;
        }

        if (message?.indexOf('(FORCE_RELOAD)') === -1) {
          this.debuggerStore.log({
            component: 'WebSocketStore',
            message: JSON.stringify(object),
            messageType: this.debuggerStore.messageType.ERROR,
            method: 'Emit > SystemMessage',
          });
        }

        if (type === -1) {
          if (message.indexOf('(FORCE_RELOAD)') > -1) {
            window.location.reload(true);
          }

          if (
            message.indexOf('(IP_BLOCKED)') > -1 ||
            message.indexOf('(KICKED_BY_ADMIN)') > -1
          ) {
            this.invalidAuctionError = true;
            this.connection
              .stop()
              .then(() => {
                clearTimeout(this.networkCheckTimeout);
                clearStorage();
                this.connection = null;
              })
              .catch((error) => console.error('SystemMessage', error));
            window.location.href = '/login';
          }
        }

        if (message.includes('Session Token is Invalid')) {
          this.authStore.setProperty(
            'errorMessage',
            'Invalid username/password',
          );
          // console.log(
          //   '*****disconnect initiated***** line 716',
          //   object.payload,
          // );

          this.disconnect();
          clearStorage();
          this.toggleSoftLoading(false);
          this.auctionStore.setProperty('connected', false);
        }

        setTimeout(() => this.messageStore.clearSystemMessage(), 3000);
        break;
      }
      case 'UiMessageFromClerk': {
        this.bidStore.handleUiMessageCodes(object.payload[1]);

        const debugging = this.bidStore.auction?.debugging;

        if (debugging) {
          if (debugging === 'DebuggingErrorsOff') {
            //this.bidStore.setProperty('debugging', null);
            this.debuggerStore.handleDisableLogging();
            // this.debuggerStore.setDebugItem(
            //   storageKeys.writeLogToDevWindow,
            //   false,
            //   false,
            // );
            // this.debuggerStore.setDebugItem(
            //   storageKeys.readLogWhenConnected,
            //   true,
            //   false,
            // );
            // this.debuggerStore.setDebugItem(
            //   storageKeys.writeToLogWhenConnected,
            //   false,
            //   false,
            // );
            // this.debuggerStore.setDebugItem(
            //   storageKeys.writeLogToConsole,
            //   false,
            //   false,
            // );
            // this.debuggerStore.setDebugItem(
            //   storageKeys.sendTimestampsToEngine,
            //   false,
            //   false,
            // );
            // this.debuggerStore.setDebugItem(
            //   storageKeys.writeTimestampsToConsole,
            //   false,
            //   false,
            // );
            // this.debuggerStore.setDebugItem(
            //   storageKeys.deleteOnBlockChange,
            //   false,
            //   false,
            // );
            // this.debuggerStore.setDebugItem(
            //   storageKeys.skipPingPongWhenConnected,
            //   true,
            //   false,
            // );
            // this.debuggerStore.setDebugItem(
            //   storageKeys.maxLogStorageSizeInKb,
            //   1000,
            //   false,
            // );
            // this.debuggerStore.setDebugItem(
            //   storageKeys.maxMessageLength,
            //   150,
            //   false,
            // );
            // this.debuggerStore.setDebugItem(
            //   storageKeys.engineLogLevel,
            //   'off',
            //   false,
            // );
            // this.debuggerStore.setDebugItem(
            //   storageKeys.uiLogLevel,
            //   'off',
            //   false,
            // );

            // this.debuggerStore.deleteLog();

            // this.debuggerStore.setUpdated();
            return;
          }

          if (debugging === 'DebuggingErrorsOn') {
            //this.bidStore.setProperty('debugging', null);
            this.debuggerStore.handleDefaultSettings();

            return;
          }
        }

        break;
      }
      default: {
        console.error('Unhandled WebSocket message received', object);
        break;
      }
    }
  };

  receiveMessage = ({ method = null, payload = [] }) => {
    this.sendMessageTimesToAuditLog(method, 'receive');
    if (method !== "pong") {
      console.log('**** MESSAGE');
      console.log(method, payload);
      console.log('****');
    }

    if (this.debuggerStore.checkPingPong(method)) {
      this.debuggerStore.log({
        component: 'WebSocketStore',
        message: `Received=${method}; Payload=${JSON.stringify(payload).substr(
          0,
          this.debuggerStore.maxMessageLength,
        )}`,
        messageType: this.debuggerStore.messageType.INFO,
        method: `receiveMessage - ${method}`,
      });
    }
  };

  sendMessage = async ({ method = null, payload = [] }) => {
    if (!method) throw new Error('Method required');
    const modPayload = [...payload.map((parm) => parm?.toString())];
    modPayload.unshift(method);

    if (this.debuggerStore.checkPingPong(method)) {
      this.debuggerStore.log({
        component: 'WebSocketStore',
        message: `Method=${method}; Payload=${JSON.stringify(payload).substr(
          0,
          this.debuggerStore.maxMessageLength,
        )}`,
        messageType: this.debuggerStore.messageType.INFO,
        method: `sendMessage - ${method}`,
      });
    }

    this.sendMessageTimesToAuditLog(method, 'send');

    this.connection.invoke(...modPayload).catch((error) => {
      console.error('sendMessage', { error, method });
      if (this.debuggerStore.checkPingPong(method)) {
        this.debuggerStore.log({
          component: 'WebSocketStore',
          message: error.toString(),
          messageType: this.debuggerStore.messageType.ERROR,
          method: `sendMessage - ${method}`,
        });
      }
    });
  };

  sendMessageTimesToAuditLog = (method, action) => {
    const auctionId = this.auctionStore.auctionID;
    const laneId = this.auctionStore.getAuctionLaneIdForInvocation;

    if (
      this.debuggerStore.isDebugMode &&
      auctionId &&
      laneId &&
      method !== 'ping' &&
      method !== 'pong'
    ) {
      if (this.debuggerStore.getDebugBooleanValue('sendTimestampsToAuditLog')) {
        const message = `${method} ${action} ${formatDateTimeMS()}`;

        if (
          this.debuggerStore.getDebugBooleanValue('showTimestampsInConsole')
        ) {
          console.log(
            `%c AuditLog > ${message}`,
            `background: ${action === 'send' ? 'blue' : 'green'}; color: white`,
          );
        }

        this.sendToAuditLog(message);
      }
    }
  };

  sendToAuditLog = (message) => {
    const auctionId = this.auctionStore.auctionID;
    const laneId = this.auctionStore.getAuctionLaneIdForInvocation;

    if (
      this.debuggerStore.getDebugProperty('debugMode') &&
      auctionId &&
      laneId
    ) {
      this.connection
        .invoke('AuditLog', auctionId, laneId, message)
        .catch(console.error);
    }
  };

  sendClearClientViewOnlyMessage = (laneId, account, member) =>
    this.sendMessage({
      method: 'ClearClientViewOnly',
      payload: [laneId, account, member],
    });

  sendSetClientViewOnlyMessage = (laneId, account, member) =>
    this.sendMessage({
      method: 'SetClientViewOnly',
      payload: [laneId, account, member],
    });

  sendFocusBidButtonMessage = () => {
    this.bidStore.auction.lastUIStateChangeTimestamp = this.keyGen();
    if (!this.bidStore.auction.focusOnBidButton) {
      this.bidStore.auction.focusOnProductId = false;
      this.bidStore.auction.focusOnFloorBidderBadge = false;
      this.bidStore.auction.selectFloorBidderBadge = false;
      this.bidStore.auction.focusOnStartingBidAmount = false;
      this.bidStore.auction.focusOnBidButton = true;
      this.bidStore.auction.focusOnSidePanelSearch = false;
      this.bidStore.auction.focusOnIndvMessage = false;
      this.bidStore.auction.focusOnLaneMessage = false;
      this.bidStore.auction.focusOnPinMessage = false;
    }
  };

  sendFocusFloorBidderBadgeMessage = () => {
    this.bidStore.auction.lastUIStateChangeTimestamp = this.keyGen();
    if (!this.bidStore.auction.focusOnFloorBidderBadge) {
      this.bidStore.auction.focusOnProductId = false;
      this.bidStore.auction.isEditingFloorBidderBadge = true;
      this.bidStore.auction.focusOnFloorBidderBadge = true;
      this.bidStore.auction.focusOnStartingBidAmount = false;
      this.bidStore.auction.focusOnBidButton = false;
      this.bidStore.auction.focusOnSidePanelSearch = false;
      this.bidStore.auction.focusOnIndvMessage = false;
      this.bidStore.auction.focusOnLaneMessage = false;
      this.bidStore.auction.focusOnPinMessage = false;
    }
  };

  sendDirectMessageToUserMessage = () => {
    this.bidStore.auction.lastUIStateChangeTimestamp = this.keyGen();
    console.log(
      'sendDirectMessageToUserMessage > focusOnIndvMessage',
      this.bidStore.auction.focusOnIndvMessage,
    );
    if (!this.bidStore.auction.focusOnIndvMessage) {
      this.bidStore.auction.focusOnProductId = false;
      this.bidStore.auction.isEditingFloorBidderBadge = false;
      this.bidStore.auction.focusOnFloorBidderBadge = false;
      this.bidStore.auction.focusOnStartingBidAmount = false;
      this.bidStore.auction.focusOnBidButton = false;
      this.bidStore.auction.focusOnSidePanelSearch = false;
      this.bidStore.auction.focusOnIndvMessage = true;
      this.bidStore.auction.focusOnLaneMessage = false;
      this.bidStore.auction.focusOnPinMessage = false;
    }
  };

  sendLaneMessageToUsersMessage = () => {
    this.bidStore.auction.lastUIStateChangeTimestamp = this.keyGen();
    console.log(
      'sendLaneMessageToUsersMessage > focusOnLaneMessage',
      this.bidStore.auction.focusOnLaneMessage,
    );

    if (!this.bidStore.auction.focusOnLaneMessage) {
      this.bidStore.auction.focusOnProductId = false;
      this.bidStore.auction.isEditingFloorBidderBadge = false;
      this.bidStore.auction.focusOnFloorBidderBadge = false;
      this.bidStore.auction.focusOnStartingBidAmount = false;
      this.bidStore.auction.focusOnBidButton = false;
      this.bidStore.auction.focusOnSidePanelSearch = false;
      this.bidStore.auction.focusOnIndvMessage = false;
      this.bidStore.auction.focusOnLaneMessage = true;
      this.bidStore.auction.focusOnPinMessage = false;
    }
  };

  sendPinMessageToUsersMessage = () => {
    this.bidStore.auction.lastUIStateChangeTimestamp = this.keyGen();
    // console.log(
    //   'sendPinMessageToUsersMessage > focusOnPinMessage',
    //   this.bidStore.auction.focusOnPinMessage,
    // );

    if (!this.bidStore.auction.focusOnPinMessage) {
      this.bidStore.auction.focusOnProductId = false;
      this.bidStore.auction.isEditingFloorBidderBadge = false;
      this.bidStore.auction.focusOnFloorBidderBadge = false;
      this.bidStore.auction.focusOnStartingBidAmount = false;
      this.bidStore.auction.focusOnBidButton = false;
      this.bidStore.auction.focusOnSidePanelSearch = false;
      this.bidStore.auction.focusOnIndvMessage = false;
      this.bidStore.auction.focusOnLaneMessage = false;
      this.bidStore.auction.focusOnPinMessage = true;
    }
  };

  sendFocusOpenItemMessage = () => {
    this.bidStore.auction.lastUIStateChangeTimestamp = this.keyGen();
    if (!this.bidStore.auction.focusOnProductId) {
      this.bidStore.auction.focusOnProductId = true;
      this.bidStore.auction.focusOnFloorBidderBadge = false;
      this.bidStore.auction.selectFloorBidderBadge = false;
      this.bidStore.auction.focusOnStartingBidAmount = false;
      this.bidStore.auction.focusOnBidButton = false;
      this.bidStore.auction.focusOnSidePanelSearch = false;
      this.bidStore.auction.focusOnIndvMessage = false;
      this.bidStore.auction.focusOnLaneMessage = false;
      this.bidStore.auction.focusOnPinMessage = false;
    }
  };

  sendFocusStartingBidAmountMessage = () => {
    this.bidStore.auction.lastUIStateChangeTimestamp = this.keyGen();
    if (!this.bidStore.auction.focusOnStartingBidAmount) {
      this.bidStore.auction.focusOnProductId = false;
      this.bidStore.auction.focusOnFloorBidderBadge = false;
      this.bidStore.auction.selectFloorBidderBadge = false;
      this.bidStore.auction.focusOnStartingBidAmount = true;
      this.bidStore.auction.focusOnBidButton = false;
      this.bidStore.auction.focusOnSidePanelSearch = false;
      this.bidStore.auction.focusOnIndvMessage = false;
      this.bidStore.auction.focusOnLaneMessage = false;
      this.bidStore.auction.focusOnPinMessage = false;
    }
  };

  sendRemoveFocusMessage = () => {
    this.bidStore.auction.lastUIStateChangeTimestamp = this.keyGen();
    if (
      this.bidStore.auction.focusOnStartingBidAmount ||
      this.bidStore.auction.focusOnFloorBidderBadge ||
      this.bidStore.auction.focusOnBidButton ||
      this.bidStore.auction.focusOnProductId ||
      this.bidStore.auction.focusOnIndvMessage ||
      this.bidStore.auction.focusOnLaneMessage ||
      this.bidStore.auction.focusOnPinMessage
    ) {
      this.bidStore.auction.focusOnProductId = false;
      this.bidStore.auction.focusOnFloorBidderBadge = false;
      this.bidStore.auction.selectFloorBidderBadge = false;
      this.bidStore.auction.focusOnStartingBidAmount = false;
      this.bidStore.auction.focusOnBidButton = false;
      this.bidStore.auction.isEditingFloorBidderBadge = false;
      this.bidStore.auction.focusOnSidePanelSearch = false;
      this.bidStore.auction.focusOnIndvMessage = false;
      this.bidStore.auction.focusOnLaneMessage = false;
      this.bidStore.auction.focusOnPinMessage = false;
    }
  };

  sendFocusSidePanelSearchMessage = () => {
    if (!this.bidStore.auction.focusOnSidePanelSearch) {
      this.bidStore.auction.focusOnSidePanelSearch = true;
      this.bidStore.auction.focusOnProductId = false;
      this.bidStore.auction.focusOnFloorBidderBadge = false;
      this.bidStore.auction.selectFloorBidderBadge = false;
      this.bidStore.auction.focusOnStartingBidAmount = false;
      this.bidStore.auction.focusOnBidButton = false;
      this.bidStore.auction.isEditingFloorBidderBadge = false;
      this.bidStore.auction.focusOnIndvMessage = false;
      this.bidStore.auction.focusOnLaneMessage = false;
      this.bidStore.auction.focusOnPinMessage = false;
    }
  };

  sendRemoveModalFocusMessage = () => {
    if (this.bidStore.auction.focusOnSidePanelSearch) {
      this.bidStore.auction.focusOnSidePanelSearch = false;
    }
  };

  sendSetLaneRunningMessage = async () => {
    if (this.auctionStore.laneStatus !== 'Running') {
      await this.sendMessage({
        method: 'SetLaneRunningStatus',
        payload: [this.auctionStore.getAuctionLaneIdForInvocation],
      });
    }
  };

  listeners = () => {
    const events = [
      'AuctionChanged',
      'AuctionInfo',
      'AuctionList',
      'AuctionMessage',
      'AuctionSettings',
      'BidAmountChanged',
      'BiddingClosed',
      'BiddingOpened',
      'Disconnected',
      'GlobalPinCleared',
      'GlobalPinSet',
      'LaneClients',
      'LaneBidIncrementChanged',
      'LaneChanged',
      'LaneList',
      'LaneMessage',
      'LaneMediaSettings',
      'LaneMetrics',
      'LanePinCleared',
      'LanePinSet',
      'LaneSettings',
      'LaneStatusChanged',
      'LayoutSettings',
      'MediaStatusChanged',
      'MessageFromClerks',
      'MessageFromClient',
      'OnBlockChanged',
      'Product',
      'ProviderSettings',
      'pong',
      'RunList',
      'SellerAction',
      'ServerDisconnect',
      'ServerMessage',
      'SessionSettings',
       'OnBlockCountdown',
      'SystemMessage',
      'UiMessageFromClerk',
    ];
    events.map((name) =>
      this.connection.on(name, (...payload) =>
        this.emit({ method: name, payload }),
      ),
    );
  };
}

decorate(WebSocketStore, {
  connect: action,
  connected: observable,
  connection: observable,
  data: observable,
  disconnect: action,
  endEvent: action,
  endLane: action,
  error: observable,
  handleReconnect: action,
  handleRejoin: action,
  //networkConnected: observable, //not used...
  reconnecting: observable,
  sendClearClientViewOnlyMessage: action,
  sendDirectMessageToUserMessage: action,
  sendFocusBidButtonMessage: action,
  sendFocusFloorBidderBadgeMessage: action,
  sendFocusOpenItemMessage: action,
  sendFocusSidePanelSearchMessage: action,
  sendFocusStartingBidAmountMessage: action,
  sendLaneMessageToUsersMessage: action,
  sendMessage: action,
  sendMessageTimesToAuditLog: action,
  sendPinMessageToUsersMessage: action,
  sendRemoveFocusMessage: action,
  sendRemoveModalFocusMessage: action,
  sendSetClientViewOnlyMessage: action,
  sendSetLaneRunningMessage: action,
  sendToAuditLog: action,
  setProperty: action,
  softLoadingVisible: observable,
  toggleSoftLoading: action,
});
